import { Progress, Steps } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import './AppLoadingScreen.less';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { loadingSteps } from '../../app/Slices/appLoadingSlice';
import { isNetworkError } from '../../utils/errorHandling';

interface LoadingScreenProps {
  error?: Error;
}

export const AppLoadingScreen = ({ error }: LoadingScreenProps) => {
  const loadingStep = useSelector(
    (state: RootState) => state.appLoading.loadingStep
  );
  const currentStep = Object.values(loadingSteps).indexOf(loadingStep);
  const [progress, setProgress] = useState(currentStep * 25);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        let newProgress = Math.max(prevProgress, currentStep * 25) + 5;

        // Calculate progress based on current loading step
        switch (Object.values(loadingSteps).indexOf(loadingStep)) {
          case 0: // AUTH_LOADING
            newProgress = Math.min(newProgress, 25);
            break;
          case 1: // USER_LOADING
          case 2: // TOKEN_LOADING
            newProgress = Math.min(Math.max(newProgress, 50), 75);
            break;
          case 3: // ORG_LOADING
            newProgress = Math.min(Math.max(newProgress, 75), 98);
            break;
        }

        return newProgress;
      });
    }, 200);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loadingStep]);

  return (
    <div className="auth-loading-container">
      <div>
        <Logo />
      </div>
      {error && (
        <div className="error-container">
          <h2>Connection Error</h2>
          <p className="error-subtitle">
            {isNetworkError(error)
              ? 'Server is unreachable'
              : error.message || 'An unexpected error occurred'}
          </p>
          <p className="error-instructions">
            Please check your internet connection and refresh the page to try
            again.
          </p>
          <p className="error-support">
            If the problem persists, contact{' '}
            <a href="mailto:support@cirra.ai">support@cirra.ai</a>
          </p>
        </div>
      )}
      {!error && (
        <>
          <div style={{ width: '100%', maxWidth: '800px' }}>
            <Progress percent={progress} showInfo={false} />
          </div>
          <Steps
            current={currentStep}
            items={[
              {
                title: 'Identity Verification',
                description: 'Authentication and authorization',
                icon: <UserOutlined />,
              },
              {
                title: 'Connection Setup',
                description: 'Connecting to Cirra AI servers',
                icon: <LockOutlined />,
              },
              {
                title: 'System Initialization',
                description: 'Preparing your workspace',
                icon: <SafetyOutlined />,
              },
              {
                title: 'Salesforce Connection',
                description: 'Verifying Salesforce org',
                icon: <ApiOutlined />,
              },
            ]}
            style={{ width: '100%', maxWidth: '1000px' }}
          />
        </>
      )}
    </div>
  );
};
